import React from 'react';
import {Tooltip as MuiTooltip, type TooltipProps as MuiTooltipProps} from '@mui/material';
import {type Theme as MuiTheme} from '@mui/material/styles';
import {useAriaAndDataProps} from '@famly/mf_ts-utils_data-aria-props';
import {type Theme} from '@famly/mf_theme';

export type TooltipProps = {
    /**
     * The element that triggers the tooltip
     */
    children: MuiTooltipProps['children'];

    /**
     * The content of the tooltip
     */
    tooltipContent: MuiTooltipProps['title'];

    /**
     * Placement of the tooltip
     */
    placement?: MuiTooltipProps['placement'];

    /**
     * If true, interactions with the tooltip content will close the tooltip. Default is true.
     */
    disableInteractive?: MuiTooltipProps['disableInteractive'];

    /**
     * Whether the tooltip is disabled
     */
    isDisabled?: boolean;

    /**
     * Color key for the tooltip border. Default is 'n75'.
     */
    borderColor?: keyof Theme['colorPalette'];

    /**
     * Color key for the tooltip background. Default is 'n25'.
     */
    backgroundColor?: keyof Theme['colorPalette'];

    /**
     * Color key for the tooltip text. Default is 'n400'.
     */
    color?: keyof Theme['colorPalette'];
} & DataProps;

export const Tooltip = (props: TooltipProps) => {
    const {
        children,
        tooltipContent,
        placement = 'top',
        isDisabled,
        borderColor = 'n75',
        backgroundColor = 'n25',
        color = 'n400',
        disableInteractive = true,
        ...rest
    } = props;

    const dataAriaProps = useAriaAndDataProps(props);

    const popperSx = React.useMemo(
        () => ({
            '& .MuiTooltip-tooltip': {
                border: (theme: MuiTheme) => `1px solid ${theme.modernFamlyTheme.colorPalette[borderColor]}`,
                backgroundColor: (theme: MuiTheme) => theme.modernFamlyTheme.colorPalette[backgroundColor],
                color: (theme: MuiTheme) => theme.modernFamlyTheme.colorPalette[color],
                padding: 3,
                maxWidth: 245,
            },
        }),
        [borderColor, backgroundColor, color],
    );

    return (
        <MuiTooltip
            title={tooltipContent}
            placement={placement}
            disableFocusListener={isDisabled}
            disableHoverListener={isDisabled}
            disableTouchListener={isDisabled}
            disableInteractive={disableInteractive}
            componentsProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -6],
                            },
                        },
                    ],
                    sx: popperSx,
                },
            }}
            {...rest}
            {...dataAriaProps}
        >
            {children}
        </MuiTooltip>
    );
};
